import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import "./SubwayDataPage.css";

const SubwayDataPage = () => {
  const { id: businessId } = useParams();
  const navigate = useNavigate();

  const [storeNumber, setStoreNumber] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [salesRows, setSalesRows] = useState([]);
  const [payrollRows, setPayrollRows] = useState([]);
  const [tenderRows, setTenderRows] = useState([]);
  const [tipRows, setTipRows] = useState([]);

  const [loadingStore, setLoadingStore] = useState(true);
  const [loadingSales, setLoadingSales] = useState(true);
  const [loadingPayroll, setLoadingPayroll] = useState(true);
  const [loadingTenders, setLoadingTenders] = useState(true);
  const [loadingTips, setLoadingTips] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [didAutoSetDates, setDidAutoSetDates] = useState(false);

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};



const parseDateInput = (value) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const getYesterday = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - 1);
  return d;
};

const yesterdayInputMax = useMemo(() => formatDateInput(getYesterday()), []);

const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};


 const latestExistingDataDate = useMemo(() => {
  const allDates = [];

  salesRows.forEach((row) => {
    if (row.sales_date) allDates.push(String(row.sales_date).slice(0, 10));
  });

  payrollRows.forEach((row) => {
    if (row.work_date) allDates.push(String(row.work_date).slice(0, 10));
  });

  tenderRows.forEach((row) => {
    if (row.transaction_date) allDates.push(String(row.transaction_date).slice(0, 10));
  });

  tipRows.forEach((row) => {
    if (row.tip_date) allDates.push(String(row.tip_date).slice(0, 10));
  });

  if (!allDates.length) return "";

  allDates.sort();
  return allDates[allDates.length - 1];
}, [salesRows, payrollRows, tenderRows, tipRows]);


const initialDataLoaded = useMemo(() => {
  return (
    !loadingStore &&
    !loadingSales &&
    !loadingPayroll &&
    !loadingTenders &&
    !loadingTips
  );
}, [loadingStore, loadingSales, loadingPayroll, loadingTenders, loadingTips]);



useEffect(() => {
  if (!initialDataLoaded || didAutoSetDates) return;

  const yesterday = getYesterday();

  if (latestExistingDataDate) {
    const latestDateObj = parseDateInput(latestExistingDataDate);

    if (latestDateObj) {
      if (latestDateObj.getTime() === yesterday.getTime()) {
        setStartDate("");
        setEndDate("");
        setMessage("Data is up to date.");
        setMessageType("success");
        setDidAutoSetDates(true);
        return;
      }

      if (latestDateObj > yesterday) {
        setStartDate("");
        setEndDate("");
        setMessage("Stored data is ahead of yesterday. Please review synced dates.");
        setMessageType("error");
        setDidAutoSetDates(true);
        return;
      }

      const nextStart = addDays(latestDateObj, 1);
      let calculatedEnd = addDays(nextStart, 6);

      if (calculatedEnd > yesterday) {
        calculatedEnd = yesterday;
      }

      setStartDate(formatDateInput(nextStart));
      setEndDate(formatDateInput(calculatedEnd));
      setDidAutoSetDates(true);
      return;
    }
  }

  const fallbackStart = addDays(yesterday, -6);
  setStartDate(formatDateInput(fallbackStart));
  setEndDate(formatDateInput(yesterday));
  setDidAutoSetDates(true);
}, [initialDataLoaded, didAutoSetDates, latestExistingDataDate]);
  

  const fetchBusinessStore = useCallback(async () => {
    if (!businessId || businessId === "undefined") {
      setLoadingStore(false);
      setMessage("Business ID is missing.");
      setMessageType("error");
      return "";
    }

    try {
      setLoadingStore(true);
      const response = await api.get(`/subway-data/business-store/${businessId}`);
      const resolvedStoreNumber = String(response.data?.store_number || "").trim();
      setStoreNumber(resolvedStoreNumber);
      return resolvedStoreNumber;
    } catch (err) {
      console.error("Error loading business store number:", err.response?.data || err.message);
      setMessage("Failed to load business store number.");
      setMessageType("error");
      setStoreNumber("");
      return "";
    } finally {
      setLoadingStore(false);
    }
  }, [businessId]);

  const fetchSalesData = useCallback(async (resolvedStoreNumber) => {
    if (!resolvedStoreNumber) {
      setLoadingSales(false);
      setSalesRows([]);
      return;
    }

    try {
      setLoadingSales(true);
      const response = await api.get(`/subway-data/sales/${resolvedStoreNumber}`);
      setSalesRows(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Error loading subway sales data:", err.response?.data || err.message);
      setMessage("Failed to load subway sales data.");
      setMessageType("error");
    } finally {
      setLoadingSales(false);
    }
  }, []);

  const fetchPayrollData = useCallback(async (resolvedStoreNumber) => {
    if (!resolvedStoreNumber) {
      setLoadingPayroll(false);
      setPayrollRows([]);
      return;
    }

    try {
      setLoadingPayroll(true);
      const response = await api.get(`/subway-data/payroll/${resolvedStoreNumber}`);
      setPayrollRows(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Error loading subway payroll data:", err.response?.data || err.message);
      setMessage("Failed to load subway payroll data.");
      setMessageType("error");
    } finally {
      setLoadingPayroll(false);
    }
  }, []);

  const fetchTenderData = useCallback(async (resolvedStoreNumber) => {
    if (!resolvedStoreNumber) {
      setLoadingTenders(false);
      setTenderRows([]);
      return;
    }

    try {
      setLoadingTenders(true);
      const response = await api.get(`/subway-data/tenders/${resolvedStoreNumber}`);
      setTenderRows(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Error loading subway tender data:", err.response?.data || err.message);
      setMessage("Failed to load subway tender data.");
      setMessageType("error");
    } finally {
      setLoadingTenders(false);
    }
  }, []);

  const fetchTipData = useCallback(async (resolvedStoreNumber) => {
    if (!resolvedStoreNumber) {
      setLoadingTips(false);
      setTipRows([]);
      return;
    }

    try {
      setLoadingTips(true);
      const response = await api.get(`/subway-data/tips/${resolvedStoreNumber}`);
      setTipRows(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error("Error loading subway tip data:", err.response?.data || err.message);
      setMessage("Failed to load subway tip data.");
      setMessageType("error");
    } finally {
      setLoadingTips(false);
    }
  }, []);

  useEffect(() => {
    const loadPageData = async () => {
      const resolvedStoreNumber = await fetchBusinessStore();
      await fetchSalesData(resolvedStoreNumber);
      await fetchPayrollData(resolvedStoreNumber);
      await fetchTenderData(resolvedStoreNumber);
      await fetchTipData(resolvedStoreNumber);
    };

    loadPageData();
  }, [fetchBusinessStore, fetchSalesData, fetchPayrollData, fetchTenderData, fetchTipData]);

  

  const handleSync = async () => {
  if (!startDate || !endDate) {
    setMessage("Start date and end date are required.");
    setMessageType("error");
    return;
  }

  const startObj = parseDateInput(startDate);
  const endObj = parseDateInput(endDate);
  const yesterday = getYesterday();
  const today = addDays(yesterday, 1);

  if (!startObj || !endObj) {
    setMessage("Invalid start date or end date.");
    setMessageType("error");
    return;
  }

  if (startObj > endObj) {
    setMessage("Start date cannot be after end date.");
    setMessageType("error");
    return;
  }

  if (endObj >= today) {
    setMessage("End date cannot be today or a future date. Please select yesterday or earlier.");
    setMessageType("error");
    return;
  }

  const diffTime = endObj.getTime() - startObj.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;

  if (diffDays > 7) {
    setMessage("Please sync 7 days or less at a time.");
    setMessageType("error");
    return;
  }

  try {
    setSyncing(true);
    setMessage("");
    setMessageType("");

    await api.post("/subway-data/sync", {
      start_date: startDate,
      end_date: endDate,
    });

    setMessage("Subway data synced successfully.");
    setMessageType("success");

    setDidAutoSetDates(false);

if (storeNumber) {
  await fetchSalesData(storeNumber);
  await fetchPayrollData(storeNumber);
  await fetchTenderData(storeNumber);
  await fetchTipData(storeNumber);
}
  } catch (err) {
    console.error("Error syncing subway data:", err.response?.data || err.message);
    setMessage(err.response?.data?.message || "Failed to sync subway data.");
    setMessageType("error");
  } finally {
    setSyncing(false);
  }
};

  const handleEmployeesPage = () => {
    if (!businessId || !storeNumber) {
      setMessage("Business ID or store number is missing.");
      setMessageType("error");
      return;
    }

    navigate(`/dashboard/subway-employees/${businessId}/${storeNumber}`);
  };

  const tenderTotalsByDayAndType = useMemo(() => {
    const grouped = {};

    tenderRows.forEach((row) => {
      const dateKey = row.transaction_date || "";
      const tenderType = String(row.tender_type || "").trim() || "Unknown";

      if (!dateKey) return;

      if (!grouped[dateKey]) grouped[dateKey] = {};
      if (!grouped[dateKey][tenderType]) {
        grouped[dateKey][tenderType] = {
          transaction_time: dateKey,
          tender_type: tenderType,
          total_amount: 0,
        };
      }

      grouped[dateKey][tenderType].total_amount += Number(row.amount || 0);
    });

    return Object.values(grouped)
      .sort((a, b) => new Date(b.transaction_time) - new Date(a.transaction_time))
      .flatMap((dayGroup) =>
        Object.values(dayGroup).sort((a, b) => a.tender_type.localeCompare(b.tender_type))
      );
  }, [tenderRows]);

  const tipTotalsByDayAndEmployee = useMemo(() => {
  return Array.isArray(tipRows) ? tipRows : [];
}, [tipRows]);


const formatDisplayDate = (value) => {
  if (!value) return "";

  const datePart = String(value).slice(0, 10);
  const [year, month, day] = datePart.split("-").map(Number);

  return new Date(year, month - 1, day).toLocaleDateString();
};
 

  const renderSplitTable = ({
    isLoading,
    rows,
    emptyText,
    columns,
    keyGetter,
    renderCells,
    widthClass = "",
  }) => {
    if (isLoading) {
      return <div className="subway-data-status">Loading...</div>;
    }

    if (!rows.length) {
      return <div className="subway-data-status">{emptyText}</div>;
    }

    return (
      <div className={`subway-split-table ${widthClass}`}>
        <div className="subway-split-table-head">
          <table className="subway-data-table subway-data-table-head">
            <colgroup>
              {columns.map((col) => (
                <col key={col.key} className={col.colClass || ""} />
              ))}
            </colgroup>
            <thead>
              <tr>
                {columns.map((col) => (
                  <th key={col.key}>{col.label}</th>
                ))}
              </tr>
            </thead>
          </table>
        </div>

        <div className="subway-split-table-body">
          <table className="subway-data-table subway-data-table-body">
            <colgroup>
              {columns.map((col) => (
                <col key={col.key} className={col.colClass || ""} />
              ))}
            </colgroup>
            <tbody>
              {rows.map((row) => (
                <tr key={keyGetter(row)}>{renderCells(row)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="subway-data-page">
      <div className="subway-data-card">
        <h1 className="pw-page-title">Subway Data</h1>

        {message ? (
          <div
            className={`subway-data-message ${
              messageType === "success"
                ? "subway-data-message-success"
                : "subway-data-message-error"
            }`}
          >
            {message}
          </div>
        ) : null}

        <div className="subway-data-toolbar">
          <div className="subway-data-field">
            <label className="subway-data-label">Store Number</label>
            <input type="text" className="subway-data-input" value={storeNumber} readOnly />
          </div>

          <div className="subway-data-field">
            <label className="subway-data-label">Start Date</label>
            <input
  type="date"
  className="subway-data-input"
  value={startDate}
  max={yesterdayInputMax}
  onChange={(e) => setStartDate(e.target.value)}
/>
          </div>

          <div className="subway-data-field">
            <label className="subway-data-label">End Date</label>
           <input
  type="date"
  className="subway-data-input"
  value={endDate}
  max={yesterdayInputMax}
  onChange={(e) => setEndDate(e.target.value)}
/>
          </div>

          <div className="subway-data-toolbar-actions">
            <button
              type="button"
              className="avg-cogs-btn"
              onClick={handleSync}
              disabled={syncing}
            >
              {syncing ? "Syncing..." : "Sync Data"}
            </button>

            <button
              type="button"
              className="avg-cogs-btn"
              onClick={handleEmployeesPage}
              disabled={loadingStore || !storeNumber}
            >
              Employees
            </button>

            <button
        type="button"
        className="avg-cogs-btn"
        onClick={() => navigate(`/dashboard/business/${businessId}/integration`)}
      >
        Integrations
      </button>
          </div>
        </div>

        <div className="subway-data-grid">
          <div className="subway-data-section">
            <h2 className="subway-data-section-title">Sales Data</h2>
            <div className="subway-data-section-body">
              {renderSplitTable({
                isLoading: loadingStore || loadingSales,
                rows: salesRows,
                emptyText: "No sales data found.",
                columns: [
                  { key: "date", label: "Date", colClass: "subway-col-date" },
                  { key: "netSales", label: "Net Sales", colClass: "subway-col-money" },
                  { key: "salesTax", label: "Sales Tax", colClass: "subway-col-money" },
                ],
                keyGetter: (row) => row.id,
                renderCells: (row) => (
                  <>
                    <td>{formatDisplayDate(row.sales_date)}</td>
                    <td>{Number(row.net_sales || 0).toFixed(2)}</td>
                    <td>{Number(row.sales_tax || 0).toFixed(2)}</td>
                  </>
                ),
              })}
            </div>
          </div>

          <div className="subway-data-section">
            <h2 className="subway-data-section-title">Payroll Data</h2>
            <div className="subway-data-section-body">
              {renderSplitTable({
                isLoading: loadingStore || loadingPayroll,
                rows: payrollRows,
                emptyText: "No payroll data found.",
                columns: [
                  { key: "date", label: "Date", colClass: "subway-col-date" },
                  { key: "employee", label: "Employee Name", colClass: "subway-col-name" },
                  { key: "clockIn", label: "Clock In", colClass: "subway-col-time" },
                  { key: "clockOut", label: "Clock Out", colClass: "subway-col-time" },
                  { key: "hours", label: "Hours Worked", colClass: "subway-col-hours" },
                ],
                keyGetter: (row) => row.id,
                renderCells: (row) => (
                  <>
                    <td>{formatDisplayDate(row.work_date)}</td>
                    <td>{row.employee_name}</td>
                    <td>{row.clock_in}</td>
                    <td>{row.clock_out}</td>
                    <td>{Number(row.hours_worked || 0).toFixed(2)}</td>
                  </>
                ),
              })}
            </div>
          </div>

          <div className="subway-data-section">
            <h2 className="subway-data-section-title">Tender Totals by Day and Type</h2>
            <div className="subway-data-section-body">
              {renderSplitTable({
                isLoading: loadingStore || loadingTenders,
                rows: tenderTotalsByDayAndType,
                emptyText: "No tender data found.",
                columns: [
                  { key: "date", label: "Date", colClass: "subway-col-date" },
                  { key: "type", label: "Tender Type", colClass: "subway-col-name" },
                  { key: "amount", label: "Total Amount", colClass: "subway-col-money" },
                ],
                keyGetter: (row) => `${row.transaction_time}-${row.tender_type}`,
                renderCells: (row) => (
                  <>
                    <td>{formatDisplayDate(row.transaction_time)}</td>
                    <td>{row.tender_type}</td>
                    <td>{Number(row.total_amount || 0).toFixed(2)}</td>
                  </>
                ),
              })}
            </div>
          </div>

          <div className="subway-data-section">
            <h2 className="subway-data-section-title">Tip Totals by Day and Employee</h2>
            <div className="subway-data-section-body">
              {renderSplitTable({
                isLoading: loadingStore || loadingTips,
                rows: tipTotalsByDayAndEmployee,
                emptyText: "No tip data found.",
                columns: [
  { key: "date", label: "Date", colClass: "subway-col-date" },
  { key: "employee", label: "Employee", colClass: "subway-col-name" },
  { key: "tips", label: "Split Tips", colClass: "subway-col-money" },
],
keyGetter: (row) => `${row.tip_date}-${row.employee_name}`,
renderCells: (row) => (
  <>
    <td>{formatDisplayDate(row.tip_date)}</td>
    <td>{row.employee_name}</td>
    <td>{Number(row.total_tip_amount || 0).toFixed(2)}</td>
  </>
),
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubwayDataPage;

// import React, { useCallback, useEffect, useMemo, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import api from "../services/api";
// import "./SubwayDataPage.css";

// const SubwayDataPage = () => {
//   const { id: businessId } = useParams();
//   const navigate = useNavigate();

//   const [storeNumber, setStoreNumber] = useState("");
//   const [startDate, setStartDate] = useState("");
//   const [endDate, setEndDate] = useState("");

//   const [salesRows, setSalesRows] = useState([]);
//   const [payrollRows, setPayrollRows] = useState([]);
//   const [tenderRows, setTenderRows] = useState([]);
//   const [tipRows, setTipRows] = useState([]);

//   const [loadingStore, setLoadingStore] = useState(true);
//   const [loadingSales, setLoadingSales] = useState(true);
//   const [loadingPayroll, setLoadingPayroll] = useState(true);
//   const [loadingTenders, setLoadingTenders] = useState(true);
//   const [loadingTips, setLoadingTips] = useState(true);
//   const [syncing, setSyncing] = useState(false);

//   const [message, setMessage] = useState("");
//   const [messageType, setMessageType] = useState("");

//   useEffect(() => {
//     const today = new Date();
//     const prior = new Date();
//     prior.setDate(today.getDate() - 6);

//     const formatDate = (date) => {
//       const year = date.getFullYear();
//       const month = String(date.getMonth() + 1).padStart(2, "0");
//       const day = String(date.getDate()).padStart(2, "0");
//       return `${year}-${month}-${day}`;
//     };

//     setStartDate(formatDate(prior));
//     setEndDate(formatDate(today));
//   }, []);

//   const fetchBusinessStore = useCallback(async () => {
//     if (!businessId || businessId === "undefined") {
//       setLoadingStore(false);
//       setMessage("Business ID is missing.");
//       setMessageType("error");
//       return "";
//     }

//     try {
//       setLoadingStore(true);

//       const response = await api.get(`/subway-data/business-store/${businessId}`);
//       const resolvedStoreNumber = String(response.data?.store_number || "").trim();

//       setStoreNumber(resolvedStoreNumber);
//       return resolvedStoreNumber;
//     } catch (err) {
//       console.error("Error loading business store number:", err.response?.data || err.message);
//       setMessage("Failed to load business store number.");
//       setMessageType("error");
//       setStoreNumber("");
//       return "";
//     } finally {
//       setLoadingStore(false);
//     }
//   }, [businessId]);

//   const fetchSalesData = useCallback(async (resolvedStoreNumber) => {
//     if (!resolvedStoreNumber) {
//       setLoadingSales(false);
//       setSalesRows([]);
//       return;
//     }

//     try {
//       setLoadingSales(true);
//       const response = await api.get(`/subway-data/sales/${resolvedStoreNumber}`);
//       setSalesRows(Array.isArray(response.data) ? response.data : []);
//     } catch (err) {
//       console.error("Error loading subway sales data:", err.response?.data || err.message);
//       setMessage("Failed to load subway sales data.");
//       setMessageType("error");
//     } finally {
//       setLoadingSales(false);
//     }
//   }, []);

//   const fetchPayrollData = useCallback(async (resolvedStoreNumber) => {
//     if (!resolvedStoreNumber) {
//       setLoadingPayroll(false);
//       setPayrollRows([]);
//       return;
//     }

//     try {
//       setLoadingPayroll(true);
//       const response = await api.get(`/subway-data/payroll/${resolvedStoreNumber}`);
//       setPayrollRows(Array.isArray(response.data) ? response.data : []);
//     } catch (err) {
//       console.error("Error loading subway payroll data:", err.response?.data || err.message);
//       setMessage("Failed to load subway payroll data.");
//       setMessageType("error");
//     } finally {
//       setLoadingPayroll(false);
//     }
//   }, []);

//   const fetchTenderData = useCallback(async (resolvedStoreNumber) => {
//     if (!resolvedStoreNumber) {
//       setLoadingTenders(false);
//       setTenderRows([]);
//       return;
//     }

//     try {
//       setLoadingTenders(true);
//       const response = await api.get(`/subway-data/tenders/${resolvedStoreNumber}`);
//       setTenderRows(Array.isArray(response.data) ? response.data : []);
//     } catch (err) {
//       console.error("Error loading subway tender data:", err.response?.data || err.message);
//       setMessage("Failed to load subway tender data.");
//       setMessageType("error");
//     } finally {
//       setLoadingTenders(false);
//     }
//   }, []);

 

//   const fetchTipData = useCallback(async (resolvedStoreNumber) => {
//     if (!resolvedStoreNumber) {
//       setLoadingTips(false);
//       setTipRows([]);
//       return;
//     }

//     try {
//       setLoadingTips(true);
//       const response = await api.get(`/subway-data/tips/${resolvedStoreNumber}`);
//       setTipRows(Array.isArray(response.data) ? response.data : []);
//     } catch (err) {
//       console.error("Error loading subway tip data:", err.response?.data || err.message);
//       setMessage("Failed to load subway tip data.");
//       setMessageType("error");
//     } finally {
//       setLoadingTips(false);
//     }
//   }, []);

//   useEffect(() => {
//     const loadPageData = async () => {
//       const resolvedStoreNumber = await fetchBusinessStore();
//       await fetchSalesData(resolvedStoreNumber);
//       await fetchPayrollData(resolvedStoreNumber);
//       await fetchTenderData(resolvedStoreNumber);
//       await fetchTipData(resolvedStoreNumber);
//     };

//     loadPageData();
//   }, [fetchBusinessStore, fetchSalesData, fetchPayrollData, fetchTenderData, fetchTipData]);

//   const handleSync = async () => {
//     if (!startDate || !endDate) {
//       setMessage("Start date and end date are required.");
//       setMessageType("error");
//       return;
//     }

//     try {
//       setSyncing(true);
//       setMessage("");
//       setMessageType("");

//       await api.post("/subway-data/sync", {
//         start_date: startDate,
//         end_date: endDate
//       });

//       setMessage("Subway data synced successfully.");
//       setMessageType("success");

//       if (storeNumber) {
//         await fetchSalesData(storeNumber);
//         await fetchPayrollData(storeNumber);
//         await fetchTenderData(storeNumber);
//         await fetchTipData(storeNumber);
//       }
//     } catch (err) {
//       console.error("Error syncing subway data:", err.response?.data || err.message);
//       setMessage(err.response?.data?.message || "Failed to sync subway data.");
//       setMessageType("error");
//     } finally {
//       setSyncing(false);
//     }
//   };

 

//   const handleEmployeesPage = () => {
//     if (!businessId || !storeNumber) {
//       setMessage("Business ID or store number is missing.");
//       setMessageType("error");
//       return;
//     }

//     navigate(`/dashboard/subway-employees/${businessId}/${storeNumber}`);
//   };

//   const tenderTotalsByDayAndType = useMemo(() => {
//     const grouped = {};

//     tenderRows.forEach((row) => {
//       const dateKey = row.transaction_date || "";
//       const tenderType = String(row.tender_type || "").trim() || "Unknown";

//       if (!dateKey) return;

//       if (!grouped[dateKey]) {
//         grouped[dateKey] = {};
//       }

//       if (!grouped[dateKey][tenderType]) {
//         grouped[dateKey][tenderType] = {
//           transaction_time: dateKey,
//           tender_type: tenderType,
//           total_amount: 0,
//           total_change_amount: 0,
//           total_tip_amount: 0
//         };
//       }

//       grouped[dateKey][tenderType].total_amount += Number(row.amount || 0);
//       grouped[dateKey][tenderType].total_change_amount += Number(row.change_amount || 0);
//       grouped[dateKey][tenderType].total_tip_amount += Number(row.tip_amount || 0);
//     });

//     return Object.values(grouped)
//       .sort((a, b) => (a.transaction_time < b.transaction_time ? 1 : -1))
//       .flatMap((dayGroup) =>
//         Object.values(dayGroup).sort((a, b) => a.tender_type.localeCompare(b.tender_type))
//       );
//   }, [tenderRows]);

//   const tipTotalsByDayAndEmployee = useMemo(() => {
//     const grouped = {};

//     tipRows.forEach((row) => {
//       const dateKey = row.tip_date || "";
//       const employeeName = String(row.clerk_name || "").trim() || "Unknown";

//       if (!dateKey) return;

//       if (!grouped[dateKey]) {
//         grouped[dateKey] = {};
//       }

//       if (!grouped[dateKey][employeeName]) {
//         grouped[dateKey][employeeName] = {
//           tip_date: dateKey,
//           clerk_name: employeeName,
//           total_tip_amount: 0,
//           total_employee_split_tip: 0,
//           total_amount_tendered: 0,
//           total_total: 0,
//           total_tax: 0
//         };
//       }

//       grouped[dateKey][employeeName].total_tip_amount += Number(row.tip_amount || 0);
//       grouped[dateKey][employeeName].total_employee_split_tip += Number(row.employee_split_tip || 0);
//       grouped[dateKey][employeeName].total_amount_tendered += Number(row.amount_tendered || 0);
//       grouped[dateKey][employeeName].total_total += Number(row.total || 0);
//       grouped[dateKey][employeeName].total_tax += Number(row.tax || 0);
//     });

//     return Object.values(grouped)
//       .sort((a, b) => (a.tip_date < b.tip_date ? 1 : -1))
//       .flatMap((dayGroup) =>
//         Object.values(dayGroup).sort((a, b) => a.clerk_name.localeCompare(b.clerk_name))
//       );
//   }, [tipRows]);

//   return (
//     <div className="subway-data-page">
//       <div className="subway-data-card">
//         <h1 className="subway-data-title">Subway Data</h1>

//         {message ? (
//           <div
//             className={`subway-data-message ${
//               messageType === "success"
//                 ? "subway-data-message-success"
//                 : "subway-data-message-error"
//             }`}
//           >
//             {message}
//           </div>
//         ) : null}

//         <div className="subway-data-toolbar">
//           <div className="subway-data-field">
//             <label className="subway-data-label">Store Number</label>
//             <input
//               type="text"
//               className="subway-data-input"
//               value={storeNumber}
//               readOnly
//             />
//           </div>

//           <div className="subway-data-field">
//             <label className="subway-data-label">Start Date</label>
//             <input
//               type="date"
//               className="subway-data-input"
//               value={startDate}
//               onChange={(e) => setStartDate(e.target.value)}
//             />
//           </div>

//           <div className="subway-data-field">
//             <label className="subway-data-label">End Date</label>
//             <input
//               type="date"
//               className="subway-data-input"
//               value={endDate}
//               onChange={(e) => setEndDate(e.target.value)}
//             />
//           </div>

//           <div className="subway-data-toolbar-actions">
//           <div>
//             <button
//               type="button"
//               className="avg-cogs-btn"
//               onClick={handleSync}
//               disabled={syncing}
//             >
//               {syncing ? "Syncing..." : "Sync Data"}
//             </button>
//             </div>

//             <div>
//             <button
//               type="button"
//               className="avg-cogs-btn"
//               onClick={handleEmployeesPage}
//               disabled={loadingStore || !storeNumber}
//             >
//               Employees
//             </button>
//             </div>
//           </div>
//         </div>
// <div className="subway-data-grid">
//         <div className="subway-data-section">
//           <h2 className="subway-data-section-title">Sales Data</h2>

//           {loadingStore || loadingSales ? (
//             <div className="subway-data-loading">Loading sales data...</div>
//           ) : salesRows.length === 0 ? (
//             <div className="subway-data-empty">No sales data found.</div>
//           ) : (
//             <div className="subway-data-table-wrap">
//               <table className="subway-data-table">
//                 <thead>
//                   <tr>
//                     <th>Date</th>
                    
//                     <th>Net Sales</th>
//                     <th>Sales Tax</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {salesRows.map((row) => (
//                     <tr key={row.id}>
//                       <td>{new Date(row.sales_date).toLocaleDateString()}</td>
                      
//                       <td>{Number(row.net_sales || 0).toFixed(2)}</td>
//                       <td>{Number(row.sales_tax || 0).toFixed(2)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>

//         <div className="subway-data-section">
//           <h2 className="subway-data-section-title">Payroll Data</h2>

//           {loadingStore || loadingPayroll ? (
//             <div className="subway-data-loading">Loading payroll data...</div>
//           ) : payrollRows.length === 0 ? (
//             <div className="subway-data-empty">No payroll data found.</div>
//           ) : (
//             <div className="subway-data-table-wrap">
//               <table className="subway-data-table">
//                 <thead>
//                   <tr>
//                     <th>Date</th>
                  
//                     <th>Employee Name</th>
//                     <th>Clock In</th>
//                     <th>Clock Out</th>
//                     <th>Hours Worked</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {payrollRows.map((row) => (
//                     <tr key={row.id}>
//                       <td>{new Date(row.work_date).toLocaleDateString()}</td>
                     
//                       <td>{row.employee_name}</td>
//                       <td>{row.clock_in}</td>
//                       <td>{row.clock_out}</td>
//                       <td>{Number(row.hours_worked || 0).toFixed(2)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>

//         <div className="subway-data-section">
//           <h2 className="subway-data-section-title">Tender Totals by Day and Type</h2>

//           {loadingStore || loadingTenders ? (
//             <div className="subway-data-loading">Loading tender data...</div>
//           ) : tenderTotalsByDayAndType.length === 0 ? (
//             <div className="subway-data-empty">No tender data found.</div>
//           ) : (
//             <div className="subway-data-table-wrap">
//               <table className="subway-data-table">
//                 <thead>
//                   <tr>
//                     <th>Date</th>
//                     <th>Tender Type</th>
//                     <th>Total Amount</th>
                   
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {tenderTotalsByDayAndType.map((row) => (
//                     <tr key={`${row.transaction_time}-${row.tender_type}`}>
//                       <td>{new Date(row.transaction_time).toLocaleDateString()}</td>
//                       <td>{row.tender_type}</td>
//                       <td>{Number(row.total_amount || 0).toFixed(2)}</td>
                     
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>

//         <div className="subway-data-section">
//           <h2 className="subway-data-section-title">Tip Totals by Day and Employee</h2>

//           {loadingStore || loadingTips ? (
//             <div className="subway-data-loading">Loading tip data...</div>
//           ) : tipTotalsByDayAndEmployee.length === 0 ? (
//             <div className="subway-data-empty">No tip data found.</div>
//           ) : (
//             <div className="subway-data-table-wrap">
//               <table className="subway-data-table">
//                 <thead>
//                   <tr>
//                     <th>Date</th>
                    
//                     <th>Total Tips</th>
                    
                   
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {tipTotalsByDayAndEmployee.map((row) => (
//                     <tr key={`${row.tip_date}-${row.clerk_name}`}>
//                       <td>{new Date(row.tip_date).toLocaleDateString()}</td>
                      
//                       <td>{Number(row.total_tip_amount || 0).toFixed(2)}</td>
                      
                     
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SubwayDataPage;


// import React, { useCallback, useEffect, useMemo, useState } from "react";
// import { useParams } from "react-router-dom";
// import api from "../services/api";
// import "./SubwayDataPage.css";

// const SubwayDataPage = () => {
//   const { id: businessId } = useParams();

//   const [storeNumber, setStoreNumber] = useState("");
//   const [startDate, setStartDate] = useState("");
//   const [endDate, setEndDate] = useState("");

//   const [salesRows, setSalesRows] = useState([]);
//   const [payrollRows, setPayrollRows] = useState([]);
//   const [tenderRows, setTenderRows] = useState([]);
//   const [tipRows, setTipRows] = useState([]);

//   const [loadingStore, setLoadingStore] = useState(true);
//   const [loadingSales, setLoadingSales] = useState(true);
//   const [loadingPayroll, setLoadingPayroll] = useState(true);
//   const [loadingTenders, setLoadingTenders] = useState(true);
//   const [loadingTips, setLoadingTips] = useState(true);
//   const [syncing, setSyncing] = useState(false);

//   const [message, setMessage] = useState("");
//   const [messageType, setMessageType] = useState("");

//   useEffect(() => {
//     const today = new Date();
//     const prior = new Date();
//     prior.setDate(today.getDate() - 6);

//     const formatDate = (date) => {
//       const year = date.getFullYear();
//       const month = String(date.getMonth() + 1).padStart(2, "0");
//       const day = String(date.getDate()).padStart(2, "0");
//       return `${year}-${month}-${day}`;
//     };

//     setStartDate(formatDate(prior));
//     setEndDate(formatDate(today));
//   }, []);

//   const fetchBusinessStore = useCallback(async () => {
//     if (!businessId || businessId === "undefined") {
//       setLoadingStore(false);
//       setMessage("Business ID is missing.");
//       setMessageType("error");
//       return "";
//     }

//     try {
//       setLoadingStore(true);

//       const response = await api.get(`/subway-data/business-store/${businessId}`);
//       const resolvedStoreNumber = String(response.data?.store_number || "").trim();

//       setStoreNumber(resolvedStoreNumber);
//       return resolvedStoreNumber;
//     } catch (err) {
//       console.error("Error loading business store number:", err.response?.data || err.message);
//       setMessage("Failed to load business store number.");
//       setMessageType("error");
//       setStoreNumber("");
//       return "";
//     } finally {
//       setLoadingStore(false);
//     }
//   }, [businessId]);

//   const fetchSalesData = useCallback(async (resolvedStoreNumber) => {
//     if (!resolvedStoreNumber) {
//       setLoadingSales(false);
//       setSalesRows([]);
//       return;
//     }

//     try {
//       setLoadingSales(true);

//       const response = await api.get(`/subway-data/sales/${resolvedStoreNumber}`);
//       setSalesRows(Array.isArray(response.data) ? response.data : []);
//     } catch (err) {
//       console.error("Error loading subway sales data:", err.response?.data || err.message);
//       setMessage("Failed to load subway sales data.");
//       setMessageType("error");
//     } finally {
//       setLoadingSales(false);
//     }
//   }, []);

//   const fetchPayrollData = useCallback(async (resolvedStoreNumber) => {
//     if (!resolvedStoreNumber) {
//       setLoadingPayroll(false);
//       setPayrollRows([]);
//       return;
//     }

//     try {
//       setLoadingPayroll(true);

//       const response = await api.get(`/subway-data/payroll/${resolvedStoreNumber}`);
//       setPayrollRows(Array.isArray(response.data) ? response.data : []);
//     } catch (err) {
//       console.error("Error loading subway payroll data:", err.response?.data || err.message);
//       setMessage("Failed to load subway payroll data.");
//       setMessageType("error");
//     } finally {
//       setLoadingPayroll(false);
//     }
//   }, []);

//   const fetchTenderData = useCallback(async (resolvedStoreNumber) => {
//     if (!resolvedStoreNumber) {
//       setLoadingTenders(false);
//       setTenderRows([]);
//       return;
//     }

//     try {
//       setLoadingTenders(true);

//       const response = await api.get(`/subway-data/tenders/${resolvedStoreNumber}`);
//       setTenderRows(Array.isArray(response.data) ? response.data : []);
//     } catch (err) {
//       console.error("Error loading subway tender data:", err.response?.data || err.message);
//       setMessage("Failed to load subway tender data.");
//       setMessageType("error");
//     } finally {
//       setLoadingTenders(false);
//     }
//   }, []);

//   const fetchTipData = useCallback(async (resolvedStoreNumber) => {
//     if (!resolvedStoreNumber) {
//       setLoadingTips(false);
//       setTipRows([]);
//       return;
//     }

//     try {
//       setLoadingTips(true);

//       const response = await api.get(`/subway-data/tips/${resolvedStoreNumber}`);
//       setTipRows(Array.isArray(response.data) ? response.data : []);
//     } catch (err) {
//       console.error("Error loading subway tip data:", err.response?.data || err.message);
//       setMessage("Failed to load subway tip data.");
//       setMessageType("error");
//     } finally {
//       setLoadingTips(false);
//     }
//   }, []);

//   useEffect(() => {
//     const loadPageData = async () => {
//       const resolvedStoreNumber = await fetchBusinessStore();
//       await fetchSalesData(resolvedStoreNumber);
//       await fetchPayrollData(resolvedStoreNumber);
//       await fetchTenderData(resolvedStoreNumber);
//       await fetchTipData(resolvedStoreNumber);
//     };

//     loadPageData();
//   }, [fetchBusinessStore, fetchSalesData, fetchPayrollData, fetchTenderData, fetchTipData]);

//   const handleSync = async () => {
//     if (!startDate || !endDate) {
//       setMessage("Start date and end date are required.");
//       setMessageType("error");
//       return;
//     }

//     try {
//       setSyncing(true);
//       setMessage("");
//       setMessageType("");

//       await api.post("/subway-data/sync", {
//         start_date: startDate,
//         end_date: endDate
//       });

//       setMessage("Subway data synced successfully.");
//       setMessageType("success");

//       if (storeNumber) {
//         await fetchSalesData(storeNumber);
//         await fetchPayrollData(storeNumber);
//         await fetchTenderData(storeNumber);
//         await fetchTipData(storeNumber);
//       }
//     } catch (err) {
//       console.error("Error syncing subway data:", err.response?.data || err.message);
//       setMessage(err.response?.data?.message || "Failed to sync subway data.");
//       setMessageType("error");
//     } finally {
//       setSyncing(false);
//     }
//   };

//   const tenderTotalsByDayAndType = useMemo(() => {
//     const grouped = {};

//     tenderRows.forEach((row) => {
//       const dateKey = row.transaction_date || "";
//       const tenderType = String(row.tender_type || "").trim() || "Unknown";

//       if (!dateKey) return;

//       if (!grouped[dateKey]) {
//         grouped[dateKey] = {};
//       }

//       if (!grouped[dateKey][tenderType]) {
//         grouped[dateKey][tenderType] = {
//           transaction_time: dateKey,
//           tender_type: tenderType,
//           total_amount: 0,
//           total_change_amount: 0,
//           total_tip_amount: 0
//         };
//       }

//       grouped[dateKey][tenderType].total_amount += Number(row.amount || 0);
//       grouped[dateKey][tenderType].total_change_amount += Number(row.change_amount || 0);
//       grouped[dateKey][tenderType].total_tip_amount += Number(row.tip_amount || 0);
//     });

//     return Object.values(grouped)
//       .sort((a, b) => (a.transaction_time < b.transaction_time ? 1 : -1))
//       .flatMap((dayGroup) =>
//         Object.values(dayGroup).sort((a, b) => a.tender_type.localeCompare(b.tender_type))
//       );
//   }, [tenderRows]);

//   const tipTotalsByDayAndEmployee = useMemo(() => {
//     const grouped = {};

//     tipRows.forEach((row) => {
//       const dateKey = row.tip_date || "";
//       const employeeName = String(row.clerk_name || "").trim() || "Unknown";

//       if (!dateKey) return;

//       if (!grouped[dateKey]) {
//         grouped[dateKey] = {};
//       }

//       if (!grouped[dateKey][employeeName]) {
//         grouped[dateKey][employeeName] = {
//           tip_date: dateKey,
//           clerk_name: employeeName,
//           total_tip_amount: 0,
//           total_employee_split_tip: 0,
//           total_amount_tendered: 0,
//           total_total: 0,
//           total_tax: 0
//         };
//       }

//       grouped[dateKey][employeeName].total_tip_amount += Number(row.tip_amount || 0);
//       grouped[dateKey][employeeName].total_employee_split_tip += Number(row.employee_split_tip || 0);
//       grouped[dateKey][employeeName].total_amount_tendered += Number(row.amount_tendered || 0);
//       grouped[dateKey][employeeName].total_total += Number(row.total || 0);
//       grouped[dateKey][employeeName].total_tax += Number(row.tax || 0);
//     });

//     return Object.values(grouped)
//       .sort((a, b) => (a.tip_date < b.tip_date ? 1 : -1))
//       .flatMap((dayGroup) =>
//         Object.values(dayGroup).sort((a, b) => a.clerk_name.localeCompare(b.clerk_name))
//       );
//   }, [tipRows]);

//   return (
//     <div className="subway-data-page">
//       <div className="subway-data-card">
//         <h1 className="subway-data-title">Subway Data</h1>

//         {message ? (
//           <div
//             className={`subway-data-message ${
//               messageType === "success"
//                 ? "subway-data-message-success"
//                 : "subway-data-message-error"
//             }`}
//           >
//             {message}
//           </div>
//         ) : null}

//         <div className="subway-data-toolbar">
//           <div className="subway-data-field">
//             <label className="subway-data-label">Store Number</label>
//             <input
//               type="text"
//               className="subway-data-input"
//               value={storeNumber}
//               readOnly
//             />
//           </div>

//           <div className="subway-data-field">
//             <label className="subway-data-label">Start Date</label>
//             <input
//               type="date"
//               className="subway-data-input"
//               value={startDate}
//               onChange={(e) => setStartDate(e.target.value)}
//             />
//           </div>

//           <div className="subway-data-field">
//             <label className="subway-data-label">End Date</label>
//             <input
//               type="date"
//               className="subway-data-input"
//               value={endDate}
//               onChange={(e) => setEndDate(e.target.value)}
//             />
//           </div>

//           <div className="subway-data-toolbar-actions">
//             <button
//               type="button"
//               className="subway-data-sync-btn"
//               onClick={handleSync}
//               disabled={syncing}
//             >
//               {syncing ? "Syncing..." : "Sync Data"}
//             </button>
//           </div>
//         </div>

//         <div className="subway-data-section">
//           <h2 className="subway-data-section-title">Sales Data</h2>

//           {loadingStore || loadingSales ? (
//             <div className="subway-data-loading">Loading sales data...</div>
//           ) : salesRows.length === 0 ? (
//             <div className="subway-data-empty">No sales data found.</div>
//           ) : (
//             <div className="subway-data-table-wrap">
//               <table className="subway-data-table">
//                 <thead>
//                   <tr>
//                     <th>Date</th>
//                     <th>Store Number</th>
//                     <th>Net Sales</th>
//                     <th>Sales Tax</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {salesRows.map((row) => (
//                     <tr key={row.id}>
//                       <td>{new Date(row.sales_date).toLocaleDateString()}</td>
//                       <td>{row.store_number}</td>
//                       <td>{Number(row.net_sales || 0).toFixed(2)}</td>
//                       <td>{Number(row.sales_tax || 0).toFixed(2)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>

//         <div className="subway-data-section">
//           <h2 className="subway-data-section-title">Payroll Data</h2>

//           {loadingStore || loadingPayroll ? (
//             <div className="subway-data-loading">Loading payroll data...</div>
//           ) : payrollRows.length === 0 ? (
//             <div className="subway-data-empty">No payroll data found.</div>
//           ) : (
//             <div className="subway-data-table-wrap">
//               <table className="subway-data-table">
//                 <thead>
//                   <tr>
//                     <th>Date</th>
//                     <th>Store Number</th>
//                     <th>Employee Name</th>
//                     <th>Clock In</th>
//                     <th>Clock Out</th>
//                     <th>Hours Worked</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {payrollRows.map((row) => (
//                     <tr key={row.id}>
//                       <td>{new Date(row.work_date).toLocaleDateString()}</td>
//                       <td>{row.store_number}</td>
//                       <td>{row.employee_name}</td>
//                       <td>{row.clock_in ? new Date(row.clock_in).toLocaleString() : ""}</td>
//                       <td>{row.clock_out ? new Date(row.clock_out).toLocaleString() : ""}</td>
//                       <td>{Number(row.hours_worked || 0).toFixed(2)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>

//         <div className="subway-data-section">
//           <h2 className="subway-data-section-title">Tender Totals by Day and Type</h2>

//           {loadingStore || loadingTenders ? (
//             <div className="subway-data-loading">Loading tender data...</div>
//           ) : tenderTotalsByDayAndType.length === 0 ? (
//             <div className="subway-data-empty">No tender data found.</div>
//           ) : (
//             <div className="subway-data-table-wrap">
//               <table className="subway-data-table">
//                 <thead>
//                   <tr>
//                     <th>Date</th>
//                     <th>Tender Type</th>
//                     <th>Total Amount</th>
//                     <th>Total Change</th>
//                     <th>Total Tips</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {tenderTotalsByDayAndType.map((row) => (
//                     <tr key={`${row.transaction_time}-${row.tender_type}`}>
//                       <td>{new Date(row.transaction_time).toLocaleDateString()}</td>
//                       <td>{row.tender_type}</td>
//                       <td>{Number(row.total_amount || 0).toFixed(2)}</td>
//                       <td>{Number(row.total_change_amount || 0).toFixed(2)}</td>
//                       <td>{Number(row.total_tip_amount || 0).toFixed(2)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>

//         <div className="subway-data-section">
//           <h2 className="subway-data-section-title">Tip Totals by Day and Employee</h2>

//           {loadingStore || loadingTips ? (
//             <div className="subway-data-loading">Loading tip data...</div>
//           ) : tipTotalsByDayAndEmployee.length === 0 ? (
//             <div className="subway-data-empty">No tip data found.</div>
//           ) : (
//             <div className="subway-data-table-wrap">
//               <table className="subway-data-table">
//                 <thead>
//                   <tr>
//                     <th>Date</th>
//                     <th>Employee</th>
//                     <th>Total Tips</th>
//                     <th>Total Split Tips</th>
//                     <th>Amount Tendered</th>
//                     <th>Total Sales</th>
//                     <th>Total Tax</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {tipTotalsByDayAndEmployee.map((row) => (
//                     <tr key={`${row.tip_date}-${row.clerk_name}`}>
//                       <td>{new Date(row.tip_date).toLocaleDateString()}</td>
//                       <td>{row.clerk_name}</td>
//                       <td>{Number(row.total_tip_amount || 0).toFixed(2)}</td>
//                       <td>{Number(row.total_employee_split_tip || 0).toFixed(2)}</td>
//                       <td>{Number(row.total_amount_tendered || 0).toFixed(2)}</td>
//                       <td>{Number(row.total_total || 0).toFixed(2)}</td>
//                       <td>{Number(row.total_tax || 0).toFixed(2)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SubwayDataPage;



// import React, { useCallback, useEffect, useMemo, useState } from "react";
// import { useParams } from "react-router-dom";
// import api from "../services/api";
// import "./SubwayDataPage.css";

// const SubwayDataPage = () => {
//   const { id: businessId } = useParams();

//   const [storeNumber, setStoreNumber] = useState("");
//   const [startDate, setStartDate] = useState("");
//   const [endDate, setEndDate] = useState("");

//   const [salesRows, setSalesRows] = useState([]);
//   const [payrollRows, setPayrollRows] = useState([]);
//   const [tenderRows, setTenderRows] = useState([]);

//   const [loadingStore, setLoadingStore] = useState(true);
//   const [loadingSales, setLoadingSales] = useState(true);
//   const [loadingPayroll, setLoadingPayroll] = useState(true);
//   const [loadingTenders, setLoadingTenders] = useState(true);
//   const [syncing, setSyncing] = useState(false);

//   const [message, setMessage] = useState("");
//   const [messageType, setMessageType] = useState("");

//   useEffect(() => {
//     const today = new Date();
//     const prior = new Date();
//     prior.setDate(today.getDate() - 6);

//     const formatDate = (date) => {
//       const year = date.getFullYear();
//       const month = String(date.getMonth() + 1).padStart(2, "0");
//       const day = String(date.getDate()).padStart(2, "0");
//       return `${year}-${month}-${day}`;
//     };

//     setStartDate(formatDate(prior));
//     setEndDate(formatDate(today));
//   }, []);

//   const fetchBusinessStore = useCallback(async () => {
//     if (!businessId || businessId === "undefined") {
//       setLoadingStore(false);
//       setMessage("Business ID is missing.");
//       setMessageType("error");
//       return "";
//     }

//     try {
//       setLoadingStore(true);

//       const response = await api.get(`/subway-data/business-store/${businessId}`);
//       const resolvedStoreNumber = String(response.data?.store_number || "").trim();

//       setStoreNumber(resolvedStoreNumber);
//       return resolvedStoreNumber;
//     } catch (err) {
//       console.error("Error loading business store number:", err.response?.data || err.message);
//       setMessage("Failed to load business store number.");
//       setMessageType("error");
//       setStoreNumber("");
//       return "";
//     } finally {
//       setLoadingStore(false);
//     }
//   }, [businessId]);

//   const fetchSalesData = useCallback(async (resolvedStoreNumber) => {
//     if (!resolvedStoreNumber) {
//       setLoadingSales(false);
//       setSalesRows([]);
//       return;
//     }

//     try {
//       setLoadingSales(true);

//       const response = await api.get(`/subway-data/sales/${resolvedStoreNumber}`);
//       setSalesRows(Array.isArray(response.data) ? response.data : []);
//     } catch (err) {
//       console.error("Error loading subway sales data:", err.response?.data || err.message);
//       setMessage("Failed to load subway sales data.");
//       setMessageType("error");
//     } finally {
//       setLoadingSales(false);
//     }
//   }, []);

//   const fetchPayrollData = useCallback(async (resolvedStoreNumber) => {
//     if (!resolvedStoreNumber) {
//       setLoadingPayroll(false);
//       setPayrollRows([]);
//       return;
//     }

//     try {
//       setLoadingPayroll(true);

//       const response = await api.get(`/subway-data/payroll/${resolvedStoreNumber}`);
//       setPayrollRows(Array.isArray(response.data) ? response.data : []);
//     } catch (err) {
//       console.error("Error loading subway payroll data:", err.response?.data || err.message);
//       setMessage("Failed to load subway payroll data.");
//       setMessageType("error");
//     } finally {
//       setLoadingPayroll(false);
//     }
//   }, []);

//   const fetchTenderData = useCallback(async (resolvedStoreNumber) => {
//     if (!resolvedStoreNumber) {
//       setLoadingTenders(false);
//       setTenderRows([]);
//       return;
//     }

//     try {
//       setLoadingTenders(true);

//       const response = await api.get(`/subway-data/tenders/${resolvedStoreNumber}`);
//       setTenderRows(Array.isArray(response.data) ? response.data : []);
//     } catch (err) {
//       console.error("Error loading subway tender data:", err.response?.data || err.message);
//       setMessage("Failed to load subway tender data.");
//       setMessageType("error");
//     } finally {
//       setLoadingTenders(false);
//     }
//   }, []);

//   useEffect(() => {
//     const loadPageData = async () => {
//       const resolvedStoreNumber = await fetchBusinessStore();
//       await fetchSalesData(resolvedStoreNumber);
//       await fetchPayrollData(resolvedStoreNumber);
//       await fetchTenderData(resolvedStoreNumber);
//     };

//     loadPageData();
//   }, [fetchBusinessStore, fetchSalesData, fetchPayrollData, fetchTenderData]);

//   const handleSync = async () => {
//     if (!startDate || !endDate) {
//       setMessage("Start date and end date are required.");
//       setMessageType("error");
//       return;
//     }

//     try {
//       setSyncing(true);
//       setMessage("");
//       setMessageType("");

//       await api.post("/subway-data/sync", {
//         start_date: startDate,
//         end_date: endDate
//       });

//       setMessage("Subway data synced successfully.");
//       setMessageType("success");

//       if (storeNumber) {
//         await fetchSalesData(storeNumber);
//         await fetchPayrollData(storeNumber);
//         await fetchTenderData(storeNumber);
//       }
//     } catch (err) {
//       console.error("Error syncing subway data:", err.response?.data || err.message);
//       setMessage(err.response?.data?.message || "Failed to sync subway data.");
//       setMessageType("error");
//     } finally {
//       setSyncing(false);
//     }
//   };

//   const tenderTotalsByDayAndType = useMemo(() => {
//     const grouped = {};

//     tenderRows.forEach((row) => {
//       const dateKey = row.transaction_date
//         ? new Date(row.transaction_date).toISOString().slice(0, 10)
//         : "";
//       const tenderType = String(row.tender_type || "").trim() || "Unknown";

//       if (!dateKey) return;

//       if (!grouped[dateKey]) {
//         grouped[dateKey] = {};
//       }

//       if (!grouped[dateKey][tenderType]) {
//         grouped[dateKey][tenderType] = {
//           transaction_date: dateKey,
//           tender_type: tenderType,
//           total_amount: 0,
//           total_change_amount: 0,
//           total_tip_amount: 0
//         };
//       }

//       grouped[dateKey][tenderType].total_amount += Number(row.amount || 0);
//       grouped[dateKey][tenderType].total_change_amount += Number(row.change_amount || 0);
//       grouped[dateKey][tenderType].total_tip_amount += Number(row.tip_amount || 0);
//     });

//     return Object.values(grouped)
//       .sort((a, b) => (a.transaction_date < b.transaction_date ? 1 : -1))
//       .flatMap((dayGroup) =>
//         Object.values(dayGroup).sort((a, b) => a.tender_type.localeCompare(b.tender_type))
//       );
//   }, [tenderRows]);

//   return (
//     <div className="subway-data-page">
//       <div className="subway-data-card">
//         <h1 className="subway-data-title">Subway Data</h1>

//         {message ? (
//           <div
//             className={`subway-data-message ${
//               messageType === "success"
//                 ? "subway-data-message-success"
//                 : "subway-data-message-error"
//             }`}
//           >
//             {message}
//           </div>
//         ) : null}

//         <div className="subway-data-toolbar">
//           <div className="subway-data-field">
//             <label className="subway-data-label">Store Number</label>
//             <input
//               type="text"
//               className="subway-data-input"
//               value={storeNumber}
//               readOnly
//             />
//           </div>

//           <div className="subway-data-field">
//             <label className="subway-data-label">Start Date</label>
//             <input
//               type="date"
//               className="subway-data-input"
//               value={startDate}
//               onChange={(e) => setStartDate(e.target.value)}
//             />
//           </div>

//           <div className="subway-data-field">
//             <label className="subway-data-label">End Date</label>
//             <input
//               type="date"
//               className="subway-data-input"
//               value={endDate}
//               onChange={(e) => setEndDate(e.target.value)}
//             />
//           </div>

//           <div className="subway-data-toolbar-actions">
//             <button
//               type="button"
//               className="subway-data-sync-btn"
//               onClick={handleSync}
//               disabled={syncing}
//             >
//               {syncing ? "Syncing..." : "Sync Data"}
//             </button>
//           </div>
//         </div>

//         <div className="subway-data-section">
//           <h2 className="subway-data-section-title">Sales Data</h2>

//           {loadingStore || loadingSales ? (
//             <div className="subway-data-loading">Loading sales data...</div>
//           ) : salesRows.length === 0 ? (
//             <div className="subway-data-empty">No sales data found.</div>
//           ) : (
//             <div className="subway-data-table-wrap">
//               <table className="subway-data-table">
//                 <thead>
//                   <tr>
//                     <th>Date</th>
//                     <th>Store Number</th>
//                     <th>Net Sales</th>
//                     <th>Sales Tax</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {salesRows.map((row) => (
//                     <tr key={row.id}>
//                       <td>{new Date(row.sales_date).toLocaleDateString()}</td>
//                       <td>{row.store_number}</td>
//                       <td>{Number(row.net_sales || 0).toFixed(2)}</td>
//                       <td>{Number(row.sales_tax || 0).toFixed(2)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>

//         <div className="subway-data-section">
//           <h2 className="subway-data-section-title">Payroll Data</h2>

//           {loadingStore || loadingPayroll ? (
//             <div className="subway-data-loading">Loading payroll data...</div>
//           ) : payrollRows.length === 0 ? (
//             <div className="subway-data-empty">No payroll data found.</div>
//           ) : (
//             <div className="subway-data-table-wrap">
//               <table className="subway-data-table">
//                 <thead>
//                   <tr>
//                     <th>Date</th>
//                     <th>Store Number</th>
//                     <th>Employee Name</th>
//                     <th>Clock In</th>
//                     <th>Clock Out</th>
//                     <th>Hours Worked</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {payrollRows.map((row) => (
//                     <tr key={row.id}>
//                       <td>{new Date(row.work_date).toLocaleDateString()}</td>
//                       <td>{row.store_number}</td>
//                       <td>{row.employee_name}</td>
//                       <td>{row.clock_in ? new Date(row.clock_in).toLocaleString() : ""}</td>
//                       <td>{row.clock_out ? new Date(row.clock_out).toLocaleString() : ""}</td>
//                       <td>{Number(row.hours_worked || 0).toFixed(2)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>

//         <div className="subway-data-section">
//           <h2 className="subway-data-section-title">Tender Totals by Day and Type</h2>

//           {loadingStore || loadingTenders ? (
//             <div className="subway-data-loading">Loading tender data...</div>
//           ) : tenderTotalsByDayAndType.length === 0 ? (
//             <div className="subway-data-empty">No tender data found.</div>
//           ) : (
//             <div className="subway-data-table-wrap">
//               <table className="subway-data-table">
//                 <thead>
//                   <tr>
//                     <th>Date</th>
//                     <th>Tender Type</th>
//                     <th>Total Amount</th>
//                     <th>Total Change</th>
//                     <th>Total Tips</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {tenderTotalsByDayAndType.map((row) => (
//                     <tr key={`${row.transaction_date}-${row.tender_type}`}>
//                       <td>{new Date(row.transaction_date).toLocaleDateString()}</td>
//                       <td>{row.tender_type}</td>
//                       <td>{Number(row.total_amount || 0).toFixed(2)}</td>
//                       <td>{Number(row.total_change_amount || 0).toFixed(2)}</td>
//                       <td>{Number(row.total_tip_amount || 0).toFixed(2)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SubwayDataPage;



// import React, { useCallback, useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import api from "../services/api";
// import "./SubwayDataPage.css";

// const SubwayDataPage = () => {
//   const { id: businessId } = useParams();

//   const [startDate, setStartDate] = useState("");
//   const [endDate, setEndDate] = useState("");

//   const [salesRows, setSalesRows] = useState([]);
//   const [payrollRows, setPayrollRows] = useState([]);

//   const [loadingSales, setLoadingSales] = useState(true);
//   const [loadingPayroll, setLoadingPayroll] = useState(true);
//   const [syncing, setSyncing] = useState(false);

//   const [message, setMessage] = useState("");
//   const [messageType, setMessageType] = useState("");

//   useEffect(() => {
//     const today = new Date();
//     const prior = new Date();
//     prior.setDate(today.getDate() - 6);

//     const formatDate = (date) => {
//       const year = date.getFullYear();
//       const month = String(date.getMonth() + 1).padStart(2, "0");
//       const day = String(date.getDate()).padStart(2, "0");
//       return `${year}-${month}-${day}`;
//     };

//     setStartDate(formatDate(prior));
//     setEndDate(formatDate(today));
//   }, []);

//   const fetchSalesData = useCallback(async () => {
//     if (!businessId || businessId === "undefined") {
//       setLoadingSales(false);
//       setMessage("Business ID is missing.");
//       setMessageType("error");
//       return;
//     }

//     try {
//       setLoadingSales(true);

//       const response = await api.get(`/subway-data/sales/${businessId}`);
//       setSalesRows(Array.isArray(response.data) ? response.data : []);
//     } catch (err) {
//       console.error("Error loading subway sales data:", err.response?.data || err.message);
//       setMessage("Failed to load subway sales data.");
//       setMessageType("error");
//     } finally {
//       setLoadingSales(false);
//     }
//   }, [businessId]);

//   const fetchPayrollData = useCallback(async () => {
//     if (!businessId || businessId === "undefined") {
//       setLoadingPayroll(false);
//       setMessage("Business ID is missing.");
//       setMessageType("error");
//       return;
//     }

//     try {
//       setLoadingPayroll(true);

//       const response = await api.get(`/subway-data/payroll/${businessId}`);
//       setPayrollRows(Array.isArray(response.data) ? response.data : []);
//     } catch (err) {
//       console.error("Error loading subway payroll data:", err.response?.data || err.message);
//       setMessage("Failed to load subway payroll data.");
//       setMessageType("error");
//     } finally {
//       setLoadingPayroll(false);
//     }
//   }, [businessId]);

//   useEffect(() => {
//     fetchSalesData();
//     fetchPayrollData();
//   }, [fetchSalesData, fetchPayrollData]);

//   const handleSync = async () => {
//     if (!startDate || !endDate) {
//       setMessage("Start date and end date are required.");
//       setMessageType("error");
//       return;
//     }

//     try {
//       setSyncing(true);
//       setMessage("");
//       setMessageType("");

//       await api.post("/subway-data/sync", {
//         start_date: startDate,
//         end_date: endDate
//       });

//       setMessage("Subway data synced successfully.");
//       setMessageType("success");

//       await fetchSalesData();
//       await fetchPayrollData();
//     } catch (err) {
//       console.error("Error syncing subway data:", err.response?.data || err.message);
//       setMessage(err.response?.data?.message || "Failed to sync subway data.");
//       setMessageType("error");
//     } finally {
//       setSyncing(false);
//     }
//   };

//   return (
//     <div className="subway-data-page">
//       <div className="subway-data-card">
//         <h1 className="subway-data-title">Subway Data</h1>

//         {message ? (
//           <div
//             className={`subway-data-message ${
//               messageType === "success"
//                 ? "subway-data-message-success"
//                 : "subway-data-message-error"
//             }`}
//           >
//             {message}
//           </div>
//         ) : null}

//         <div className="subway-data-toolbar">
//           <div className="subway-data-field">
//             <label className="subway-data-label">Start Date</label>
//             <input
//               type="date"
//               className="subway-data-input"
//               value={startDate}
//               onChange={(e) => setStartDate(e.target.value)}
//             />
//           </div>

//           <div className="subway-data-field">
//             <label className="subway-data-label">End Date</label>
//             <input
//               type="date"
//               className="subway-data-input"
//               value={endDate}
//               onChange={(e) => setEndDate(e.target.value)}
//             />
//           </div>

//           <div className="subway-data-toolbar-actions">
//             <button
//               type="button"
//               className="subway-data-sync-btn"
//               onClick={handleSync}
//               disabled={syncing}
//             >
//               {syncing ? "Syncing..." : "Sync Data"}
//             </button>
//           </div>
//         </div>

//         <div className="subway-data-section">
//           <h2 className="subway-data-section-title">Sales Data</h2>

//           {loadingSales ? (
//             <div className="subway-data-loading">Loading sales data...</div>
//           ) : salesRows.length === 0 ? (
//             <div className="subway-data-empty">No sales data found.</div>
//           ) : (
//             <div className="subway-data-table-wrap">
//               <table className="subway-data-table">
//                 <thead>
//                   <tr>
//                     <th>Date</th>
//                     <th>Store Number</th>
//                     <th>Net Sales</th>
//                     <th>Sales Tax</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {salesRows.map((row) => (
//                     <tr key={row.id}>
//                       <td>{new Date(row.sales_date).toLocaleDateString()}</td>
//                       <td>{row.store_number}</td>
//                       <td>{Number(row.net_sales || 0).toFixed(2)}</td>
//                       <td>{Number(row.sales_tax || 0).toFixed(2)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>

//         <div className="subway-data-section">
//           <h2 className="subway-data-section-title">Payroll Data</h2>

//           {loadingPayroll ? (
//             <div className="subway-data-loading">Loading payroll data...</div>
//           ) : payrollRows.length === 0 ? (
//             <div className="subway-data-empty">No payroll data found.</div>
//           ) : (
//             <div className="subway-data-table-wrap">
//               <table className="subway-data-table">
//                 <thead>
//                   <tr>
//                     <th>Date</th>
//                     <th>Store Number</th>
//                     <th>Employee Name</th>
//                     <th>Hours Worked</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {payrollRows.map((row) => (
//                     <tr key={row.id}>
//                       <td>{new Date(row.work_date).toLocaleDateString()}</td>
//                       <td>{row.store_number}</td>
//                       <td>{row.employee_name}</td>
//                       <td>{Number(row.hours_worked || 0).toFixed(2)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SubwayDataPage;



// import React, { useCallback, useEffect, useState } from "react";
// import { useParams } from "react-router-dom";
// import api from "../services/api";
// import "./SubwayDataPage.css";

// const SubwayDataPage = () => {
//   const { id: businessId } = useParams();

//   const [salesRows, setSalesRows] = useState([]);
//   const [payrollRows, setPayrollRows] = useState([]);

//   const [loadingSales, setLoadingSales] = useState(true);
//   const [loadingPayroll, setLoadingPayroll] = useState(true);

//   const [message, setMessage] = useState("");
//   const [messageType, setMessageType] = useState("");

//   const fetchSalesData = useCallback(async () => {
//     if (!businessId || businessId === "undefined") {
//       setLoadingSales(false);
//       setMessage("Business ID is missing.");
//       setMessageType("error");
//       return;
//     }

//     try {
//       setLoadingSales(true);

//       const response = await api.get(`/subway-data/sales/${businessId}`);
//       setSalesRows(Array.isArray(response.data) ? response.data : []);
//     } catch (err) {
//       console.error("Error loading subway sales data:", err.response?.data || err.message);
//       setMessage("Failed to load subway sales data.");
//       setMessageType("error");
//     } finally {
//       setLoadingSales(false);
//     }
//   }, [businessId]);

//   const fetchPayrollData = useCallback(async () => {
//     if (!businessId || businessId === "undefined") {
//       setLoadingPayroll(false);
//       setMessage("Business ID is missing.");
//       setMessageType("error");
//       return;
//     }

//     try {
//       setLoadingPayroll(true);

//       const response = await api.get(`/subway-data/payroll/${businessId}`);
//       setPayrollRows(Array.isArray(response.data) ? response.data : []);
//     } catch (err) {
//       console.error("Error loading subway payroll data:", err.response?.data || err.message);
//       setMessage("Failed to load subway payroll data.");
//       setMessageType("error");
//     } finally {
//       setLoadingPayroll(false);
//     }
//   }, [businessId]);

//   useEffect(() => {
//     fetchSalesData();
//     fetchPayrollData();
//   }, [fetchSalesData, fetchPayrollData]);

//   return (
//     <div className="subway-data-page">
//       <div className="subway-data-card">
//         <h1 className="subway-data-title">Subway Data</h1>

//         {message ? (
//           <div
//             className={`subway-data-message ${
//               messageType === "success"
//                 ? "subway-data-message-success"
//                 : "subway-data-message-error"
//             }`}
//           >
//             {message}
//           </div>
//         ) : null}

//         <div className="subway-data-section">
//           <h2 className="subway-data-section-title">Sales Data</h2>

//           {loadingSales ? (
//             <div className="subway-data-loading">Loading sales data...</div>
//           ) : salesRows.length === 0 ? (
//             <div className="subway-data-empty">No sales data found.</div>
//           ) : (
//             <div className="subway-data-table-wrap">
//               <table className="subway-data-table">
//                 <thead>
//                   <tr>
//                     <th>Date</th>
//                     <th>Store Number</th>
//                     <th>Net Sales</th>
//                     <th>Sales Tax</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {salesRows.map((row) => (
//                     <tr key={row.id}>
//                       <td>{row.sales_date}</td>
//                       <td>{row.store_number}</td>
//                       <td>{Number(row.net_sales || 0).toFixed(2)}</td>
//                       <td>{Number(row.sales_tax || 0).toFixed(2)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>

//         <div className="subway-data-section">
//           <h2 className="subway-data-section-title">Payroll Data</h2>

//           {loadingPayroll ? (
//             <div className="subway-data-loading">Loading payroll data...</div>
//           ) : payrollRows.length === 0 ? (
//             <div className="subway-data-empty">No payroll data found.</div>
//           ) : (
//             <div className="subway-data-table-wrap">
//               <table className="subway-data-table">
//                 <thead>
//                   <tr>
//                     <th>Date</th>
//                     <th>Store Number</th>
//                     <th>Employee Name</th>
//                     <th>Hours Worked</th>
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {payrollRows.map((row) => (
//                     <tr key={row.id}>
//                       <td>{row.work_date}</td>
//                       <td>{row.store_number}</td>
//                       <td>{row.employee_name}</td>
//                       <td>{Number(row.hours_worked || 0).toFixed(2)}</td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default SubwayDataPage;